import remove from 'lodash/remove';
import React from 'react';

import { NdvrEmploymentStatus } from '@ndvr/customer-api';

import { getDateRangeLabel, titleCase } from 'utils';
import { useKeywords } from 'views/admin/segmentation/keyword-queries';
import { FilterLabel, FilterStateLabels, HorizontalLayout } from 'widgets';

import { PersonFilterKeys, usePersonFiltersContext } from './PersonFiltersContext';

export const PersonFilterLabels = () => {
    return (
        <HorizontalLayout $gap={8} $wrapContent>
            <StatusLabels />
            <LoginLabels />
            <StateLabels />
            <KeywordLabels />
        </HorizontalLayout>
    );
};

const StatusLabels = () => {
    const { filters, patchFilters } = usePersonFiltersContext();

    const removeStatus = (ndvrEmploymentStatus: NdvrEmploymentStatus) =>
        patchFilters({
            [PersonFilterKeys.ndvrEmploymentStatus]: remove(
                filters.ndvrEmploymentStatus,
                (status) => ndvrEmploymentStatus !== status,
            ),
        });

    return (
        <>
            {filters.ndvrEmploymentStatus.map((ndvrEmploymentStatus) => (
                <FilterLabel
                    key={ndvrEmploymentStatus}
                    label={titleCase(ndvrEmploymentStatus)}
                    onRemove={() => removeStatus(ndvrEmploymentStatus)}
                />
            ))}
        </>
    );
};

const StateLabels = () => {
    const { filters, patchFilters } = usePersonFiltersContext();

    const removeState = (stateAbbriviation: string) =>
        patchFilters({
            [PersonFilterKeys.states]: remove(filters.states, (state) => stateAbbriviation !== state),
        });

    return <FilterStateLabels stateAbbriviations={filters.states} onRemove={removeState} />;
};

const KeywordLabels = () => {
    const { filters, patchFilters } = usePersonFiltersContext();
    const { data: allKeywords = [] } = useKeywords();

    const removeKeyword = (keywordGUID: string) =>
        patchFilters({
            [PersonFilterKeys.keywordGUIDs]: remove(filters.keywordGUIDs, (guid) => keywordGUID !== guid),
        });

    return (
        <>
            {filters.keywordGUIDs.map((keywordGUID) => {
                const keyword = allKeywords.find((k) => k.keywordGUID === keywordGUID);
                return keyword ? (
                    <FilterLabel key={keywordGUID} label={keyword.label} onRemove={() => removeKeyword(keywordGUID)} />
                ) : null;
            })}
        </>
    );
};

const LoginLabels = () => {
    const { filters, resetFilters, isLastLoginAtActive } = usePersonFiltersContext();

    return isLastLoginAtActive ? (
        <FilterLabel
            label={getDateRangeLabel('Login', filters.lastLoginAtStart, filters.lastLoginAtEnd)}
            onRemove={() => resetFilters([PersonFilterKeys.lastLoginAtStart, PersonFilterKeys.lastLoginAtEnd])}
        />
    ) : null;
};
